import api from './api';
import { STUDENT_AWARDS_ASSIGNMENTS, AWARDS_AVAILABLE, AWARDS_MY } from '../config/Config';

// Suppress verbose award-related console output outside development
/* eslint-disable no-console */
if (process.env.NODE_ENV !== 'development') {
  console.log = () => {};
  console.warn = () => {};
}
/* eslint-enable no-console */

// Base URL for all award assignments - using standardized constant from Config.js

const logRequest = (method, url, params = {}) => {
  console.log(`[API] ${method.toUpperCase()} ${url}`, params || '');
  return (response) => {
    console.log(`[API] ${method.toUpperCase()} ${url} Response:`, response);
    return response;
  };
};

const logResponse = (method, url, response) => {
  console.log(`[API] ${method.toUpperCase()} ${url} Response:`, response);
};

const handleError = (error, context = '') => {
  console.error(`[API Error] ${context}`, {
    message: error.message,
    response: error.response ? {
      status: error.response.status,
      statusText: error.response.statusText,
      data: error.response.data
    } : 'No response',
    config: {
      url: error.config?.url,
      method: error.config?.method,
      params: error.config?.params,
      data: error.config?.data
    }
  });
  throw error;
};

export const getStudentAwards = async (studentId, teacherId = null) => {
  console.log('[getStudentAwards] Fetching awards for student ID:', studentId, 'teacher ID:', teacherId);
  
  try {
    // Use the api service instead of direct fetch to ensure proper base URL and authentication
    const params = { 
      student: studentId,
      t: Date.now() // cache-busting timestamp
    };
    
    // Add teacher parameter if provided
    if (teacherId) {
      params.teacher = teacherId;
    }
    
    const response = await api.get(STUDENT_AWARDS_ASSIGNMENTS, { params });
    
    console.log('[getStudentAwards] API response:', response);
    
    // Return the data in the expected format
    if (response.data && Array.isArray(response.data)) {
      console.log(`[getStudentAwards] Found ${response.data.length} awards`);
      return { data: response.data };
    } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
      console.log(`[getStudentAwards] Found ${response.data.data.length} awards in data.data`);
      return response.data;
    } else if (response.data && response.data.results && Array.isArray(response.data.results)) {
      console.log(`[getStudentAwards] Found ${response.data.results.length} awards in data.results`);
      return { data: response.data.results };
    } else {
      console.log('[getStudentAwards] No awards array found, returning empty array');
      return { data: [] };
    }
  } catch (error) {
    console.error('[getStudentAwards] Error fetching student awards:', error);
    return { data: [] }; // Return empty data on error for consistency
  }
};

export const getAvailableAwards = async () => {
  try {
    // Make API call with cache-busting parameter
    const response = await api.get(AWARDS_AVAILABLE, {
      params: { t: Date.now() }
    }).then(logRequest('get', 'getAvailableAwards'));
    
    console.log('[getAvailableAwards] API CALL COMPLETED');
    console.log('[getAvailableAwards] Raw response type:', typeof response);
    console.log('[getAvailableAwards] Raw response:', response);
    
    // Manually extract and validate the data
    if (!response || !response.data) {
      console.error('[getAvailableAwards] No response or response.data');
      return { data: [] };
    }
    
    // Access the data property directly
    const responseData = response.data;
    console.log('[getAvailableAwards] responseData type:', typeof responseData);
    console.log('[getAvailableAwards] responseData:', responseData);
    
    // See if we got the expected format from server
    if (responseData && responseData.data && Array.isArray(responseData.data)) {
      console.log('[getAvailableAwards] Found awards array with', responseData.data.length, 'items');
      
      // CRITICAL: Force the data into the expected structure
      const forcedData = {
        data: responseData.data.map(award => ({
          id: award.id,
          category: award.category,
          description: award.description,
          image_url: award.image_url
        }))
      };
      
      console.log('[getAvailableAwards] Returning forced data structure:', forcedData);
      return forcedData;
    } else {
      // If responseData is already an array, wrap it
      if (Array.isArray(responseData)) {
        console.log('[getAvailableAwards] Response is direct array, wrapping in data object');
        return { data: responseData };
      }
      
      console.warn('[getAvailableAwards] Unexpected data format, returning empty array');
      return { data: [] };
    }
  } catch (error) {
    console.error('[getAvailableAwards] Error:', error);
    handleError(error, 'getAvailableAwards');
    return { data: [] };
  }
};

export const getMyAwards = async (params = {}) => {
  try {
    const response = await api.get(AWARDS_MY, { params })
      .then(logRequest('get', 'getMyAwards'));
    return response.data;
  } catch (error) {
    handleError(error, 'getMyAwards');
  }
};

export const assignAward = async (data) => {
  console.log('[AWARD_DEBUG] assignAward starting with data:', JSON.stringify(data, null, 2));
  
  // CRITICAL: Use the student ID provided in the data, without fallbacks
  // This ensures we use exactly the ID the caller specified
  const studentId = data.student;
  console.log('[AWARD_DEBUG] Using student ID:', studentId);
  console.log('[AWARD_DEBUG] Award ID:', data.award);
  console.log('[AWARD_DEBUG] Using STUDENT_AWARDS_ASSIGNMENTS:', STUDENT_AWARDS_ASSIGNMENTS);
  
  // Ensure award ID is valid
  if (!data.award) {
    console.error('[AWARD_DEBUG] Missing award ID');
    throw new Error('Award ID is required');
  }
  
  // Use JSON format instead of FormData (since FormData didn't work)
  const requestData = {
    student: parseInt(studentId, 10),  // Ensure integer type
    award: parseInt(data.award, 10),  // Convert award to integer
    notes: data.notes || ''
  };
  
  console.log('[AWARD_DEBUG] Final request data:', JSON.stringify(requestData, null, 2));
  console.log('[AWARD_DEBUG] About to make POST request to:', api.defaults.baseURL + STUDENT_AWARDS_ASSIGNMENTS);
  
  try {
    logRequest('POST', 'assignAward', requestData);
    
    // Use JSON format with proper content type
    const response = await api.post(STUDENT_AWARDS_ASSIGNMENTS, requestData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    logResponse('POST', 'assignAward', response);
    console.log('[AWARD_DEBUG] Server response status:', response.status);
    console.log('[AWARD_DEBUG] Server response data:', response.data);
    

    
    return response.data;
  } catch (error) {
    console.log('[AWARD_DEBUG] Server response status:', error?.response?.status || 'No status');
    console.log('[AWARD_DEBUG] Server response data:', error?.response?.data || 'No data');
    
    // If the error contains more details, log them
    if (error?.response?.data) {
      console.log('[AWARD_DEBUG] Detailed error:', JSON.stringify(error.response.data, null, 2));
      if (error.response.data.student) {
        console.log('[AWARD_DEBUG] Student validation error:', error.response.data.student);
      }
      if (error.response.data.award) {
        console.log('[AWARD_DEBUG] Award validation error:', error.response.data.award);
      }
      if (error.response.data.non_field_errors) {
        console.log('[AWARD_DEBUG] General validation error:', error.response.data.non_field_errors);
      }
    } else {
      console.log('[AWARD_DEBUG] Error object:', error);
    }
    
    throw error;
  }
};

export const updateAwardAssignment = async (id, data) => {
  try {
    const response = await api.patch(`${STUDENT_AWARDS_ASSIGNMENTS}${id}/`, data)
      .then(logRequest('patch', `updateAwardAssignment/${id}`, data));
    return response.data;
  } catch (error) {
    handleError(error, 'updateAwardAssignment');
  }
};

export const deleteAwardAssignment = async (id) => {
  try {
    const response = await api.delete(`${STUDENT_AWARDS_ASSIGNMENTS}${id}/`)
      .then(logRequest('delete', `deleteAwardAssignment/${id}`));
    return response.data;
  } catch (error) {
    handleError(error, 'deleteAwardAssignment');
  }
};